import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../api';
import LanguageSwitcher from './LanguageSwitcher';
import { useAppModal } from '../hooks/useAppModal';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [termsOpened, setTermsOpened] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsHtml, setTermsHtml] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);
  const termsRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { modal, showModal } = useAppModal();

  useEffect(() => {
    API.get('/admin/maintenance')
      .then(r => { if (r.data.maintenance) navigate('/login', { replace: true }); })
      .catch(() => {});
  }, [navigate]);

  const openTermsModal = async () => {
    setShowTermsModal(true);
    if (!termsHtml) {
      setTermsLoading(true);
      try {
        const res = await API.get('/terms-conditions');
        setTermsHtml(res.data.content || '');
      } catch {
        setTermsHtml('<p style="color:#888;text-align:center;padding:40px">Impossibile caricare i Termini e Condizioni. Riprova più tardi.</p>');
      } finally {
        setTermsLoading(false);
      }
    }
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
    setTermsOpened(true);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!termsOpened || !isTermsChecked) {
      setShaking(true);
      setTimeout(() => setShaking(false), 600);
      termsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    try {
      const res = await API.post('/signup', { email, password, nickname });
      showModal({
        type: 'success',
        title: t('signup.success_title'),
        message: res.data.message,
        onConfirm: () => navigate('/login'),
        confirmLabel: t('signup.go_to_login'),
      });
    } catch (err) {
      console.error('Error during signup:', err);
      showModal({ type: 'error', title: err.response?.data?.message || t('signup.error_generic') });
    }
  };

  const canSubmit = email && password && nickname && termsOpened && isTermsChecked;

  return (
    <>
    {modal}
    <style>{`
      @keyframes shake {
        0%,100% { transform: translateX(0); }
        20%      { transform: translateX(-6px); }
        40%      { transform: translateX(6px); }
        60%      { transform: translateX(-4px); }
        80%      { transform: translateX(4px); }
      }
      @keyframes pulse-border {
        0%,100% { box-shadow: 0 0 0 0 rgba(255,170,0,0.5); }
        50%      { box-shadow: 0 0 0 6px rgba(255,170,0,0); }
      }
    `}</style>
    <div style={{ backgroundColor: '#05006C', color: 'white', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px 0' }}>

      {/* Logo */}
      <img
        src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`}
        alt="Kuesto Logo"
        style={{ width: '150px', marginBottom: '20px' }}
      />

      {/* Signup Card */}
      <div className="card" style={{ width: '100%', maxWidth: '400px', backgroundColor: '#1a1a1a', color: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.7)' }}>
        <LanguageSwitcher />
        <h3 className="text-center" style={{ marginBottom: '20px', color: '#00ff88' }}>{t('signup.title')}</h3>
        <form onSubmit={handleSignup}>
          <div className="form-group mb-3">
            <label htmlFor="email" style={{ color: '#ccc' }}>{t('signup.email')}:</label>
            <input
              type="email"
              className="form-control"
              id="email"
              placeholder={t('signup.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ backgroundColor: '#333', color: 'white', border: '1px solid #555', padding: '10px', borderRadius: '5px' }}
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="password" style={{ color: '#ccc' }}>{t('signup.password')}:</label>
            <input
              type="password"
              className="form-control"
              id="password"
              placeholder={t('signup.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ backgroundColor: '#333', color: 'white', border: '1px solid #555', padding: '10px', borderRadius: '5px' }}
            />
          </div>
          <div className="form-group mb-4">
            <label htmlFor="nickname" style={{ color: '#ccc' }}>{t('signup.nickname')}:</label>
            <input
              type="text"
              className="form-control"
              id="nickname"
              placeholder={t('signup.nickname_placeholder')}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              required
              style={{ backgroundColor: '#333', color: 'white', border: '1px solid #555', padding: '10px', borderRadius: '5px' }}
            />
          </div>

          {/* ── Sezione Terms & Conditions ── */}
          <div
            ref={termsRef}
            style={{
              marginBottom: 16,
              borderRadius: 8,
              overflow: 'hidden',
              animation: shaking ? 'shake 0.6s ease' : 'none',
            }}
          >
            {!termsOpened ? (
              /* Stato: non ancora letti → callout prominente */
              <div style={{
                backgroundColor: 'rgba(255,170,0,0.1)',
                border: '1px solid rgba(255,170,0,0.5)',
                borderRadius: 8,
                padding: '14px 16px',
                animation: 'pulse-border 2s ease-in-out infinite',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 20 }}>📄</span>
                  <span style={{ color: '#ffaa00', fontWeight: 700, fontSize: 14 }}>
                    {t('signup.terms_required_title')}
                  </span>
                </div>
                <p style={{ color: '#ccc', fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 }}>
                  {t('signup.terms_required_desc')}
                </p>
                <button
                  type="button"
                  onClick={openTermsModal}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    backgroundColor: '#ffaa00', color: '#000',
                    fontWeight: 700, fontSize: 13,
                    padding: '8px 16px', borderRadius: 6,
                    border: 'none', cursor: 'pointer',
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <i className="bi bi-file-earmark-text" />
                  {t('signup.terms_read_btn')}
                </button>
              </div>
            ) : (
              /* Stato: PDF aperto → checkbox abilitato con feedback verde */
              <div style={{
                backgroundColor: 'rgba(0,255,136,0.07)',
                border: '1px solid rgba(0,255,136,0.35)',
                borderRadius: 8,
                padding: '12px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>✅</span>
                  <span style={{ color: '#00ff88', fontWeight: 600, fontSize: 13 }}>
                    {t('signup.terms_read_done')}
                  </span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#ccc', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    id="terms"
                    checked={isTermsChecked}
                    onChange={(e) => setIsTermsChecked(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#00ff88' }}
                  />
                  {t('signup.terms_accept')}{' '}
                  <button
                    type="button"
                    onClick={openTermsModal}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#00aaff', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                  >
                    {t('signup.terms_link')}
                  </button>
                </label>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="btn btn-primary w-100"
            style={{
              backgroundColor: canSubmit ? '#00ff88' : '#444',
              border: 'none',
              padding: '10px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '5px',
              color: canSubmit ? 'black' : '#888',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
          >
            {t('signup.button')}
          </button>
        </form>
        <div className="text-center mt-3">
          <p style={{ color: '#ccc' }}>
            {t('signup.already_account')}{' '}
            <a href="/login" style={{ color: '#00aaff', textDecoration: 'none' }}>
              {t('signup.login_link')}
            </a>
          </p>
        </div>
      </div>
    </div>

    {/* ── Modal Termini e Condizioni ── */}
    {showTermsModal && (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: '16px',
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: 10,
          width: '100%', maxWidth: 720, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          overflow: 'hidden',
        }}>
          {/* Header modale */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', borderBottom: '1px solid #ddd',
            backgroundColor: '#f8f8f8',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-file-earmark-check" style={{ color: '#05006C', fontSize: 18 }} />
              <span style={{ color: '#05006C', fontWeight: 700, fontSize: 15 }}>
                {t('signup.terms_link')}
              </span>
            </div>
            <button
              type="button"
              onClick={closeTermsModal}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', lineHeight: 1 }}
            >✕</button>
          </div>

          {/* Corpo scrollabile */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
            {termsLoading
              ? <p style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>Caricamento...</p>
              : <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
            }
          </div>

          {/* Footer */}
          <div style={{
            padding: '14px 20px', borderTop: '1px solid #ddd',
            backgroundColor: '#f8f8f8', textAlign: 'center',
          }}>
            <button
              type="button"
              onClick={closeTermsModal}
              style={{
                backgroundColor: '#05006C', color: '#fff',
                border: 'none', borderRadius: 6, padding: '10px 28px',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}
            >
              {t('signup.terms_read_done')} ✓
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Signup;
